/**
 * PageNotes Component
 * Collapsible section at the bottom of each tab for general notes/observations.
 * Supports adding and viewing page-level comments.
 */

import { useState, useCallback, useEffect } from 'react';
import { SectionCard } from './SectionCard';
import { fetchPageComments, addComment } from '@/api/commentApi';
import { formatRelativeTime } from '@/utils/formatters';
import type { CommentItem } from '@/api/commentApi';

interface PageNotesProps {
  page: string;
  title?: string;
}

export function PageNotes({ page, title }: PageNotesProps) {
  const [comments, setComments] = useState<CommentItem[]>([]);
  const [loaded, setLoaded] = useState(false);
  const [newText, setNewText] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const loadNotes = useCallback(async () => {
    if (loaded) return;
    try {
      const items = await fetchPageComments(page);
      setComments(items);
      setLoaded(true);
    } catch { /* graceful */ }
  }, [page, loaded]);

  useEffect(() => { loadNotes(); }, [loadNotes]);

  const handleSubmit = useCallback(async () => {
    if (!newText.trim() || submitting) return;
    setSubmitting(true);
    try {
      const comment = await addComment('page', page, newText.trim());
      setComments((prev) => [comment, ...prev]);
      setNewText('');
    } catch { /* graceful */ }
    finally { setSubmitting(false); }
  }, [page, newText, submitting]);

  return (
    <SectionCard
      title={title ?? 'Notes'}
      badge={
        comments.length > 0 ? (
          <span style={{ fontSize: '12px', color: '#666' }}>
            {comments.length} note{comments.length !== 1 ? 's' : ''}
          </span>
        ) : undefined
      }
    >
      <div className="page-notes">
        <div className="page-notes__input">
          <textarea
            value={newText}
            onChange={(e) => setNewText(e.target.value)}
            placeholder="Add a note for the team (accountants, SLT)..."
            rows={2}
            className="page-notes__textarea"
          />
          <button
            onClick={handleSubmit}
            disabled={!newText.trim() || submitting}
            className="page-notes__submit"
            type="button"
          >
            {submitting ? 'Posting...' : 'Add Note'}
          </button>
        </div>

        {comments.length > 0 && (
          <div className="page-notes__list">
            {comments.map((c) => (
              <div key={c.id} className="page-notes__item">
                <div className="page-notes__meta">
                  <strong>{c.authorName}</strong>
                  <span>{formatRelativeTime(c.createdAt)}</span>
                </div>
                <p className="page-notes__content">{c.content}</p>
              </div>
            ))}
          </div>
        )}
      </div>
    </SectionCard>
  );
}
